import * as z from 'zod';
import { ThreePoint, threePointSchema } from './site-simulation.model';

/**
 * Stores every value in **display units** — percent for the share/growth/turn-in fields,
 * absolute for counts and hours — so applying a set is a straight signal-set with no
 * conversion. Note this is the opposite convention to `SimulationRequest`, which is fractions.
 *
 * Deliberately carries no `bevShareSource`: that is resolved from one site's BEV-holders data,
 * so persisting it would falsely imply current-site provenance on apply.
 */
export interface SimulationParameters {
  /** Absolute vehicles/day. */
  dtv: ThreePoint;
  /** Percent (mode 4 = 4 %). */
  bevShare: ThreePoint;
  /** Percent. */
  bevGrowth: ThreePoint;
  /** Ladepunkte. */
  lp: number;
  powerKw: number;
  /** Zeitfenster, in absolute hours. */
  onRouteWindow: number;
  /** Turn-in-Rate, in percent (1.75 = 1,75 %). */
  onRouteTurnIn: number;
  /** Zeitfenster, in absolute hours. */
  destWindow: number;
  /** Turn-in-Rate, in percent. */
  destTurnIn: number;
}

/**
 * A set belongs to exactly one site — there are no global presets. All ids are strings
 * (string-coerced bigints), matching `CandidateSite`.
 */
export interface SavedSimulation {
  id: string;
  name: string;
  parameters: SimulationParameters;
  /** ISO-8601. */
  createdAt: string;
  /** The tenancy scope. */
  companyId: string;
  /** Attribution only, never a read or delete boundary. */
  userId: string;
  siteId: string;
  /** Resolved server-side; blank when the creator is soft-deleted. */
  createdBy: string;
}

/**
 * Fields supplied at creation; the server stamps id/createdAt/companyId/userId/createdBy.
 * `siteId` is client-supplied (the site being viewed) and validated server-side against the
 * company scope.
 */
export type NewSavedSimulation = Pick<
  SavedSimulation,
  'name' | 'parameters' | 'siteId'
>;

export const simulationParametersSchema = z.object({
  dtv: threePointSchema,
  bevShare: threePointSchema,
  bevGrowth: threePointSchema,
  lp: z.number(),
  powerKw: z.number(),
  onRouteWindow: z.number(),
  onRouteTurnIn: z.number(),
  destWindow: z.number(),
  destTurnIn: z.number(),
}) satisfies z.ZodType<SimulationParameters>;

export const savedSimulationSchema = z.object({
  id: z.string(),
  name: z.string(),
  parameters: simulationParametersSchema,
  createdAt: z.string(),
  companyId: z.string(),
  userId: z.string(),
  siteId: z.string(),
  createdBy: z.string(),
}) satisfies z.ZodType<SavedSimulation>;

/** Kept identical to {@link SimulationParameters}, so the helpers below are lossless copies. */
export type PendingSimulationInputs = {
  dtv: ThreePoint;
  bevShare: ThreePoint;
  bevGrowth: ThreePoint;
  lp: number;
  powerKw: number;
  onRouteWindow: number;
  onRouteTurnIn: number;
  destWindow: number;
  destTurnIn: number;
};

/** Build the persisted blob from the component's pending values. */
export function toSimulationParameters(
  pending: PendingSimulationInputs,
): SimulationParameters {
  return {
    dtv: { ...pending.dtv },
    bevShare: { ...pending.bevShare },
    bevGrowth: { ...pending.bevGrowth },
    lp: pending.lp,
    powerKw: pending.powerKw,
    onRouteWindow: pending.onRouteWindow,
    onRouteTurnIn: pending.onRouteTurnIn,
    destWindow: pending.destWindow,
    destTurnIn: pending.destTurnIn,
  };
}

/** The inverse of {@link toSimulationParameters}; the round trip must stay identical. */
export function applySimulationParameters(
  params: SimulationParameters,
): PendingSimulationInputs {
  return {
    dtv: { ...params.dtv },
    bevShare: { ...params.bevShare },
    bevGrowth: { ...params.bevGrowth },
    lp: params.lp,
    powerKw: params.powerKw,
    onRouteWindow: params.onRouteWindow,
    onRouteTurnIn: params.onRouteTurnIn,
    destWindow: params.destWindow,
    destTurnIn: params.destTurnIn,
  };
}
